import { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/lang";

export default function AppsSection({ apps }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const [smokeOrigin, setSmokeOrigin] = useState(null);
  const [navigatingTo, setNavigatingTo] = useState(null);

  const handleAppClick = useCallback((e, app) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    setSmokeOrigin({ x, y });
    setNavigatingTo(app.id);

    setTimeout(() => {
      navigate(`/app/${app.id}`);
    }, 800);
  }, [navigate]);

  const platformColors = {
    iOS: "from-[#00F0FF] to-[#00F0FF]",
    Android: "from-[#4D00FF] to-[#4D00FF]",
    Web: "from-[#FF007F] to-[#FF007F]",
  };

  return (
    <section id="apps" data-testid="apps-section" className="relative py-24 sm:py-32">
      {/* Smoky transition overlay */}
      <AnimatePresence>
        {smokeOrigin && (
          <motion.div
            className="fixed inset-0 z-[100] pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="absolute rounded-full"
              style={{
                left: smokeOrigin.x,
                top: smokeOrigin.y,
                x: "-50%",
                y: "-50%",
                background: "radial-gradient(circle, rgba(0,240,255,0.15) 0%, rgba(15,15,26,0.95) 50%, rgba(5,5,10,1) 100%)",
                backdropFilter: "blur(100px)",
                WebkitBackdropFilter: "blur(100px)",
              }}
              initial={{ width: 0, height: 0, opacity: 0 }}
              animate={{
                width: "300vmax",
                height: "300vmax",
                opacity: 1,
              }}
              transition={{
                duration: 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="label-mono mb-4">{t("apps_label")}</p>
          <h2
            data-testid="apps-heading"
            className="font-['Unbounded'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white"
          >
            {t("apps_heading")}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {apps.map((app, i) => {
            const isWide = i === 0;
            return (
              <motion.div
                key={app.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                data-testid={`app-card-${app.name.toLowerCase().replace(/\s+/g, '')}`}
                className={`${isWide ? "md:col-span-8" : "md:col-span-4"} ${
                  i === 1 ? "md:col-span-4" : ""
                } ${i === 2 ? "md:col-span-6" : ""} ${i > 2 ? "md:col-span-6" : ""} relative group cursor-pointer`}
                onClick={(e) => handleAppClick(e, app)}
              >
                <div className="glass-card rounded-3xl overflow-hidden h-full hover:-translate-y-2 transition-all duration-500 hover:border-[#00F0FF]/40 relative">
                  {/* Glow orb on hover */}
                  <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#00F0FF]/0 group-hover:bg-[#00F0FF]/10 rounded-full blur-3xl transition-all duration-500" />

                  {/* Image */}
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <img
                      src={app.image_url}
                      alt={app.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#05050A] via-[#05050A]/40 to-transparent" />
                    
                    {/* Arrow icon */}
                    <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1">
                      <ArrowUpRight className="w-5 h-5 text-[#00F0FF]" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 relative z-10">
                    <p className="label-mono mb-2">{app.category}</p>
                    <h3 className="font-['Unbounded'] text-xl sm:text-2xl font-semibold text-white mb-2 tracking-tight">
                      {app.name}
                    </h3>
                    <p className="text-[#00F0FF] text-sm font-medium mb-3 font-['JetBrains_Mono']">
                      {app.tagline}
                    </p>
                    <p className="text-[#A0A0AB] text-sm leading-relaxed mb-4 line-clamp-2">
                      {app.description}
                    </p>

                    {/* Platforms & Store Links */}
                    <div className="flex gap-2 flex-wrap items-center">
                      {app.platforms?.map((platform) => (
                        <span
                          key={platform}
                          className={`text-xs px-3 py-1 rounded-full border border-white/10 text-white/70 bg-white/5`}
                        >
                          {platform}
                        </span>
                      ))}
                      {(app.ios_link || app.android_link) && (
                        <span className="text-[10px] px-3 py-1 rounded-full border border-[#00F0FF]/20 text-[#00F0FF] bg-[#00F0FF]/5 font-['JetBrains_Mono']">
                          {t("download_available")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
